import React, { ReactNode, useEffect, useRef, useState } from 'react'

import loadable from '@loadable/component'
import { Link } from 'gatsby'
import { blogPostsData } from 'src/data/blogPosts'
import styled, { css } from 'styled-components'

import {
  Body2,
  Body3,
  DropdownLink,
  ExternalLink,
  Label,
  LabelTiny,
  ParagraphSmall,
  StyledLink,
  TitleSmall,
  TitleXS
} from 'src/components/blocks/Typography'
import {
  ButtonGroup,
  primaryCTA,
  BrandButton,
  BrandButtonWrapper
} from 'src/components/partials/Button'
import { Container } from 'src/components/partials/Container'
import Divider from 'src/components/partials/Divider'
import { Grid, Column } from 'src/components/partials/Grid'
import {
  OfficeIconSm,
  BriefcaseIconSm,
  BlogIconSm,
  HeartIconSm,
  LockIconSm,
  PhoneSm
} from 'src/components/svg/BrandIcon'
import { FlatfileLogoSVG } from 'src/components/svg/Icon'
import { BrandColors, BrandTokens, FadeColor } from 'src/utilities/Colors'
import { Size } from 'src/utilities/Devices'
import { ILink } from 'src/utilities/Interfaces'
import { useTheme } from 'src/utilities/ThemeContext'
import { FontFamily } from 'src/utilities/Type'
import { useMediaQuery } from 'src/utilities/useMediaQuery'

import { demoCTA2 } from '../partials/Button'
import FaIcon from '../partials/FaIcon'

const InvertedContext = React.createContext(false)

const DELAY_DROPDOWN_MILLIS = 0

const LogoWrapper = styled.div`
  margin-right: 2rem;
  svg {
    display: inline-block;
    position: relative;
    top: -1px;
    vertical-align: middle;
  }
`
const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-right: 2rem;
  position: relative;
  top: -1px;
`
const LogoLinkContainer = styled.a<{ hasTitle: boolean }>`
  display: inline-block;
  font-size: 0;
  img {
    display: block;
  }
  ${props =>
    props.hasTitle &&
    css`
      border-right: 1px solid ${BrandColors.pigeon200};
      margin-right: 1rem;
      padding-right: 1rem;
    `}
`
const TitleText = styled.h2`
  color: ${BrandColors.text};
  font-family: ${FontFamily.brandSerif};
  font-size: 1.0625rem;
  font-weight: 500;
  transition: color 0.2s ease-out;
  a {
    color: inherit;
  }
  &:hover {
    color: ${BrandColors.primary};
  }
`
const LogoContainer = ({
  pageTitle,
  path
}: {
  pageTitle?: string
  path?: string
}) => {
  return (
    <TitleWrapper>
      <LogoLinkContainer
        hasTitle={pageTitle && pageTitle.length > 0}
        href='/'
        title='Back to home | Flatfile'
      >
        <img
          alt='Flatfile logo'
          src='/img/contentful/3W53R0jw-flatfile-jewel.svg'
        />
      </LogoLinkContainer>
      {pageTitle && (
        <TitleText>
          <a href={path || '/'} title={`${pageTitle} | Flatfile`}>
            {pageTitle}
          </a>
        </TitleText>
      )}
    </TitleWrapper>
  )
}
const NavUL = styled.ul<{ level: number }>`
  ${props =>
    props.level === 1 &&
    css`
      padding: 1.25rem 1.5rem;
    `}

  /*
    Hide login in main nav
    -----------------------
    The log in drodown which uses loginLinkArray doesn't get populated
    with the links if I remove this log in item from the defaultLinkArray,
    so I'm hiding it with css instead.

    TODO: Fix this so that the login dropdown works without this hack.
  */
  &#link-container [data-label='Log in'] {
    display: none;
  }
`
const NavLI = styled.li<{ color?: string; level: number }>`
  color: ${BrandColors.text};
  /* display: inline-block; */
  font-family: ${FontFamily.brandPrimary};
  position: relative;

  a {
    align-items: center;
    color: ${({ color = BrandColors.text }) => color};
    display: flex;
    font-weight: 400;
    transition: color 0.25s linear;
    white-space: nowrap;
    cursor: pointer;
    padding: 0.75rem;
    line-height: 1;
    .nav-icon {
      margin-left: 0.25rem;
    }
    &:hover {
      color: ${({ color }) =>
        color === BrandColors.white
          ? `${BrandColors.white}CC`
          : `${BrandColors.midnight900}CC`};
    }
    &:after {
      content: '';
      height: 6px;
      width: 10.5px;
      background-size: cover;
      display: inline-block;
      background-color: ${({ color = BrandColors.text }) => color};
      mask-image: url(/img/chevron-down.svg);
      margin-left: 8px;
      margin-bottom: 1px;
      transition: all 0.3s;
    }
  }

  &[data-label='Pricing'] {
    a:after {
      display: none;
    }
  }

  &[data-label='Log in'] {
    margin-right: 0.5rem;
  }

  &:last-child {
    a {
      cursor: pointer;
      &:hover {
        color: ${({ color }) =>
          color === BrandColors.white
            ? `${BrandColors.white}CC`
            : BrandColors.primary};
      }
    }
  }
  svg {
    transition: all 0.25s linear;
  }
  ${props =>
    props.level === 0 &&
    css`
      display: none;
      /* padding: 0.75rem; */
      /* background: green; */
      &:hover {
        .dropdown {
          opacity: 1;
          transform: translateX(-50%) translateY(0) scale(1);
          visibility: visible;
        }
      }
      @media (min-width: 1090px) {
        display: inline-block;
        font-size: 0.925rem;
      }
      @media (min-width: ${Size.medium}) {
        font-size: 1rem;
      }
    `}
  ${props =>
    props.level === 1 &&
    css`
      color: ${BrandColors.pigeon800};
      font-size: 1rem;
      line-height: 1.8;
      margin-left: 1.5rem;
      padding: 0.5rem;
    `}
  ${props =>
    props.level === 2 &&
    css`
      display: block;
      font-size: 0.975rem;
      line-height: 1.7;
      margin-bottom: 0.5rem;
      margin-top: 0.25rem;
    `}
`
const ProductIcon = styled.span<{ fill: string }>`
  align-items: center;
  background-color: ${props =>
    props.fill ? props.fill : BrandColors.pigeon200};
  display: flex;
  justify-content: center;
  height: 2rem;
  width: 2rem;
  .concierge-icon {
    left: 1px;
    position: relative;
  }
`
const NavLink = styled(({ hideChevron, ...props }) => {
  props.onClick = e => {
    if (
      typeof window !== 'undefined' &&
      e.currentTarget.href === window.location.href
    ) {
      window.location.reload()
    }
  }
  if (props.href) {
    return <ExternalLink {...props} />
  } else {
    return <StyledLink {...props} />
  }
})<{
  hideChevron?: boolean
}>`
  position: relative;
  display: block;
  &:after {
    content: '';
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    height: 10px;
    width: 6px;
    background-size: cover;
    display: inline-block;
    background-image: ${props =>
      props.hideChevron ? '' : 'url(/img/chevron-right.svg)'};
    opacity: 0;
    transition: opacity 0.2s;
  }
  &:hover {
    &:after {
      opacity: 1;
    }
  }
`

const OverviewNavLinks1 = [
  {
    label: 'Projects',
    to: '/platform/projects/',
    desc: 'Data onboarding without the hassle - organized, private and efficient'
  },
  {
    label: 'Portal',
    to: '/platform/portal/',
    desc: 'The elegant import button for your web app'
  }
]

const OverviewNavLinks2 = [
  {
    label: 'Platform Overview',
    to: '/platform/'
  },
  {
    label: 'Workbooks',
    to: '/platform/workbooks/'
  },
  {
    label: 'Mapping',
    to: '/platform/mapping/'
  },
  {
    label: 'Orchestration',
    to: '/platform/orchestration/'
  },
  {
    label: 'Plugins',
    to: '/plugins/'
  }
]

const PlatformDropdownWrapper = styled.div``

const PlatformDropdownLinksWrapper = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 2rem;
`

const PlatformDropdownLinksCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  li {
    margin-bottom: 0.25rem;
  }

  .platformList li {
    margin-bottom: 1rem;
  }

  p {
    font-size: 0.9375rem;
    padding-right: 2rem;
  }

  a {
    padding: 0.75rem;
    margin: -0.75rem;
    height: auto;
    &:hover {
      background-color: ${BrandColors.pigeon100};
      p:first-child {
        color: ${BrandColors.primary};
        transition: all 250ms ease;
      }
    }
  }

  /* border-right: 1px solid ${BrandColors.pigeon500}; */
  padding-right: 2rem;

  &:last-child {
    border-right: none;
    padding-right: 0;
    min-width: 170px;
  }

  &:first-child {
    flex: 1;
  }
`

const PlatformDropdown = (
  <PlatformDropdownWrapper>
    <PlatformDropdownLinksWrapper>
      <PlatformDropdownLinksCol>
        <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
          Platform apps
        </Label>
        <ul className='platformList'>
          {OverviewNavLinks1.map((link, i) => (
            <li key={i}>
              <NavLink hideChevron to={link.to}>
                <div>
                  <Body2 as='p' color={BrandColors.pigeon800} fontWeight='600'>
                    {link.label}
                  </Body2>
                  <Body3 color={BrandColors.pigeon800}>{link.desc}</Body3>
                </div>
              </NavLink>
            </li>
          ))}
        </ul>
      </PlatformDropdownLinksCol>

      <PlatformDropdownLinksCol>
        <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
          Platform highlights
        </Label>

        <ul>
          {OverviewNavLinks2.map((link, i) => (
            <li key={i}>
              <NavLink hideChevron to={link.to}>
                <div>
                  <Body2 as='p' color={BrandColors.pigeon800} fontWeight='600'>
                    {link.label}
                  </Body2>
                  {link.desc && (
                    <Body3 color={BrandColors.pigeon800}>{link.desc}</Body3>
                  )}
                </div>
              </NavLink>
            </li>
          ))}
        </ul>
      </PlatformDropdownLinksCol>
    </PlatformDropdownLinksWrapper>
  </PlatformDropdownWrapper>
)

const ProductsDropdownWrapper = styled.div`
  /* width: 497px; */
  box-sizing: border-box;
  padding: 2rem;
  li {
    display: flex;
    &:first-of-type {
      margin-top: 0.75rem;
    }
  }
  ${ProductIcon} {
    flex-shrink: 0;
    margin-right: 1rem;
  }
  a {
    display: block;
    padding: 0.75rem 0;
  }
  ${Label} {
    margin-bottom: 1.5rem;
  }
  .platformList li {
    margin-bottom: 1rem;
  }
`
const ProductLink = ({
  description,
  iconPath,
  newProduct,
  title,
  to
}: {
  description: string
  iconPath: string
  newProduct?: boolean
  title: string
  to: string
}) => (
  <li>
    <NavLink hideChevron to={to}>
      <img alt='' height='48' src={iconPath} width='48' />
      <div>
        <TitleSmall as='p' color={BrandColors.pigeon800}>
          {title}{' '}
          {newProduct && (
            <LabelTiny
              as='span'
              color={BrandColors.primary}
              style={{ verticalAlign: 'middle' }}
            >
              New
            </LabelTiny>
          )}
        </TitleSmall>
        <ParagraphSmall color={BrandColors.pigeon700}>
          {description}
        </ParagraphSmall>
      </div>
    </NavLink>
  </li>
)
// const LiveDemoLinkWrapper = styled.div`
//   background-color: ${BrandColors.pigeon100};
//   padding: 0.5rem 1rem;
//   margin-top: 1rem;
//   cursor: pointer;
//   border-radius: 0.5rem;
//   transition: 0.2s all;
//   position: relative;
//   ${TitleXS}, ${DescriptionSmall} {
//     transition: color 0.3s;
//   }
//   &:hover {
//     ${TitleXS}, ${DescriptionSmall} {
//       color: ${BrandColors.primary};
//     }
//   }
//   ${DescriptionSmall} {
//     margin-top: -0.375rem;
//   }
//   svg {
//     position: absolute;
//     right: 1rem;
//     top: 50%;
//     transform: translateY(-50%);
//   }
// `
// const PlayIcon = (
//   <svg
//     fill='none'
//     height='32'
//     viewBox='0 0 32 32'
//     width='32'
//     xmlns='http://www.w3.org/2000/svg'
//   >
//     <rect fill='#F6FAFC' height='32' rx='16' width='32' />
//     <path
//       d='M13 10.6362V21.3638C13 21.5479 13.1492 21.6971 13.3333 21.6971C13.4011 21.6971 13.4673 21.6765 13.5231 21.6379L21.2708 16.2741C21.4222 16.1693 21.4599 15.9616 21.3551 15.8103C21.3323 15.7773 21.3038 15.7487 21.2708 15.7259L13.5231 10.3621C13.3717 10.2573 13.1641 10.2951 13.0593 10.4465C13.0207 10.5022 13 10.5684 13 10.6362Z'
//       fill='#667380'
//     />
//   </svg>
// )

const ProductsDropdownLinks = [
  {
    label: 'Data Conversion & Onboarding',
    to: '/platform/use-cases/data-conversion/',
    desc: 'Collaborate to convert and onboard the data your customers need'
  },
  {
    label: 'Data Preparation & Migration',
    to: '/platform/use-cases/data-migration/',
    desc: 'Prepare data for systems migration projects'
  },
  {
    label: 'Embedded Data Import',
    to: '/platform/portal/',
    desc: 'Embed a smart data uploader into an application'
  }
]

// const ProductsUL = styled.ul`
//   a {
//     margin: 0;
//     padding: 0;
//     font-weight: 500;
//     color: black;
//     margin-left: -1rem;

//     &::before {
//       content: '→';
//       font-weight: 600;
//       font-size: 1rem;
//       /* width: 0px; */
//       opacity: 0;
//       margin-right: 0.25rem;
//       transition: all 0.2s;
//     }

//     &:hover {
//       margin-left: 0;
//       &::before {
//         /* width: 1rem; */
//         opacity: 1;
//       }
//     }
//   }

//   li {
//     margin: 0 0 0.75rem;
//   }
// `

export const ProductsDropdown = (
  <ProductsDropdownWrapper>
    <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
      Flatfile For
    </Label>
    <ul className='platformList'>
      {ProductsDropdownLinks.map((link, i) => (
        <li key={i}>
          <NavLink hideChevron to={link.to}>
            <div>
              <Body2 as='p' color={BrandColors.pigeon800} fontWeight='600'>
                {link.label}
              </Body2>
              {link.desc && (
                <Body3 color={BrandColors.pigeon800}>{link.desc}</Body3>
              )}
            </div>
          </NavLink>
        </li>
      ))}
    </ul>

    {/* <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
      Use Cases
    </Label>
    <ProductsUL>
      {ProductsDropdownLinks.map((link, i) => (
        <li key={i}>
          <NavLink hideChevron to={link.to}>
            <Body3 as='span' color={BrandColors.black} fontWeight='500'>
              {link.label}
            </Body3>
          </NavLink>
        </li>
      ))}
    </ProductsUL> */}
  </ProductsDropdownWrapper>
)

const UseCasesColumn = (
  <PlatformDropdownWrapper>
    <PlatformDropdownLinksWrapper>
      <PlatformDropdownLinksCol>
        <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
          Flatfile For
        </Label>
        <ul className='platformList'>
          {ProductsDropdownLinks.map((link, i) => (
            <li key={i}>
              <NavLink hideChevron to={link.to}>
                <div>
                  <Body2 as='p' color={BrandColors.pigeon800} fontWeight='600'>
                    {link.label}
                  </Body2>
                  {link.desc && (
                    <Body3 color={BrandColors.pigeon800}>{link.desc}</Body3>
                  )}
                </div>
              </NavLink>
            </li>
          ))}
        </ul>
      </PlatformDropdownLinksCol>
    </PlatformDropdownLinksWrapper>
  </PlatformDropdownWrapper>
)

// const OverviewDropdownWrapper = styled(ProductsDropdownWrapper)`
//   /* width: 472px; */
//   box-sizing: border-box;
//   padding: 2rem;
// `

// const OverviewDropdownLinks = styled.div`
//   display: flex;
//   justify-content: flex-start;
//   gap: 1.5rem;
// `

// const OverviewUL = styled.ul<{
//   colFraction?: string
// }>`
//   display: grid;
//   grid-template-columns: ${props => props.colFraction || '1fr 1fr 1fr'};
//   gap: 1.5rem;
//   li {
//     margin: 0 !important;
//     min-height: auto;

//     a {
//       padding: 0.75rem;
//       margin: -0.75rem;
//       height: auto;

//       &:hover {
//         background-color: ${BrandColors.pigeon100};
//         p:first-child {
//           color: ${BrandColors.primary};
//           transition: all 250ms ease;
//         }
//       }
//     }
//   }

//   ${Body3} {
//     font-size: 0.875rem;
//     line-height: 1.5;
//   }

//   ${Body2} {
//     font-size: 1rem;
//     line-height: 1.5;
//   }
// `

// const OverviewDropdown = (
//   <OverviewDropdownWrapper>
//     <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
//       Explore the Flatfile Data Exchange Platform
//     </Label>
//     <OverviewDropdownLinks>
//       <OverviewUL colFraction='1fr'>
//         {OverviewNavLinks1.map((link, i) => (
//           <li key={i}>
//             <NavLink hideChevron to={link.to}>
//               <div>
//                 <Body2 as='p' color={BrandColors.pigeon800} fontWeight='600'>
//                   {link.label}
//                 </Body2>
//                 <Body3 color={BrandColors.pigeon800}>{link.desc}</Body3>
//               </div>
//             </NavLink>
//           </li>
//         ))}
//       </OverviewUL>

//       <OverviewUL
//         colFraction='1fr'
//         style={{
//           borderLeft: `1px solid ${BrandColors.pigeon500}`,
//           paddingLeft: '2rem'
//         }}
//       >
//         {OverviewNavLinks2.map((link, i) => (
//           <li key={i}>
//             <NavLink hideChevron to={link.to}>
//               <div>
//                 <Body2 as='p' color={BrandColors.pigeon800} fontWeight='600'>
//                   {link.label}
//                 </Body2>
//                 {link.desc && (
//                   <Body3 color={BrandColors.pigeon800}>{link.desc}</Body3>
//                 )}
//               </div>
//             </NavLink>
//           </li>
//         ))}
//       </OverviewUL>
//     </OverviewDropdownLinks>
//   </OverviewDropdownWrapper>
// )

const DocsLink = styled(({ main, svgHoverColor, ...props }) => (
  <NavLink {...props} />
))<{
  main?: boolean
  svgHoverColor?: string
}>`
  padding: ${props => (props.main ? '.375rem .5rem' : '.25rem .5rem')};
  svg {
    position: absolute;
    left: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    transition: all 0.2s;
    color: ${BrandColors.pigeon700};
  }
  span {
    margin-left: 2rem;
  }
  &:hover {
    background-color: ${BrandColors.pigeon100};
    color: ${BrandColors.text};
    ${props =>
      props.svgHoverColor
        ? css`
            svg {
              color: ${props.svgHoverColor};
            }
          `
        : null}
  }
`

const DocsSVG = (
  <svg
    fill='none'
    height='24'
    viewBox='0 0 24 24'
    width='24'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M13.6855 18.7082C15.9114 17.819 18.6829 17.2496 22 17C22 16.9325 22 13.1013 22 5.50631H22C22 5.23018 21.7761 5.00633 21.5 5.00633C21.4958 5.00633 21.4916 5.00639 21.4874 5.00649C18.6582 5.07812 15.8291 5.74262 13 7C13 7.0445 13 10.7925 13 18.2439H13C13 18.52 13.2239 18.7439 13.5 18.7439C13.5636 18.7439 13.6265 18.7318 13.6855 18.7082Z'
      fill='currentColor'
    />
    <g opacity='0.3'>
      <path
        d='M10.3145 18.7082C8.08861 17.819 5.31712 17.2496 2 17C2 16.9325 2 13.1013 2 5.50631H2.00002C2.00002 5.23018 2.22387 5.00633 2.5 5.00633C2.50422 5.00633 2.50844 5.00639 2.51265 5.00649C5.34177 5.07812 8.17088 5.74262 11 7C11 7.0445 11 10.7925 11 18.2439H11C11 18.52 10.7762 18.7439 10.5 18.7439C10.4365 18.7439 10.3735 18.7318 10.3145 18.7082Z'
        fill='currentColor'
      />
    </g>
  </svg>
)

const RocketSVG = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    fill='currentColor'
    viewBox='0 0 256 256'
  >
    <path d='M227.85 46.89a20 20 0 00-18.74-18.74c-13.13-.77-46.65.42-74.48 28.24L131 60H74.36a19.83 19.83 0 00-14.14 5.86l-34.35 34.33a20 20 0 0011.35 33.95l37.14 5.18 42.32 42.32 5.19 37.18A19.88 19.88 0 00135.34 235a20.13 20.13 0 006.37 1 19.9 19.9 0 0014.1-5.87l34.34-34.35a19.85 19.85 0 005.85-14.14V125l3.6-3.59c27.83-27.87 29.02-61.41 28.25-74.52zM76 84h31l-31.25 31.28-27.23-3.8zm75.6-10.63A72.27 72.27 0 01204 52a72.17 72.17 0 01-21.38 52.41L128 159l-31-31zM172 180l-27.49 27.49-3.8-27.23L172 149zm-71.67 21.62C91.67 213.65 74.16 228 40 228a12 12 0 01-12-12c0-34.15 14.35-51.66 26.39-60.33a12 12 0 0114 19.47c-6 4.31-12.89 12.5-15.42 27.87 15.37-2.53 23.56-9.44 27.87-15.42a12 12 0 1119.47 14z'></path>
  </svg>
)

const Docs2SVG = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    fill='currentColor'
    viewBox='0 0 256 256'
  >
    <path d='M224 44h-64a43.86 43.86 0 00-32 13.85A43.86 43.86 0 0096 44H32a20 20 0 00-20 20v128a20 20 0 0020 20h64a20 20 0 0120 20 12 12 0 0024 0 20 20 0 0120-20h64a20 20 0 0020-20V64a20 20 0 00-20-20zM96 188H36V68h60a20 20 0 0120 20v104.81A43.79 43.79 0 0096 188zm124 0h-60a43.71 43.71 0 00-20 4.83V88a20 20 0 0120-20h60z'></path>
  </svg>
)

const APISVG = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    fill='currentColor'
    viewBox='0 0 256 256'
  >
    <path d='M71.68 97.22L34.74 128l36.94 30.78a12 12 0 11-15.36 18.44l-48-40a12 12 0 010-18.44l48-40a12 12 0 0115.36 18.44zm176 21.56l-48-40a12 12 0 10-15.36 18.44L221.26 128l-36.94 30.78a12 12 0 1015.36 18.44l48-40a12 12 0 000-18.44zM164.1 28.72a12 12 0 00-15.38 7.18l-64 176a12 12 0 007.18 15.37 11.79 11.79 0 004.1.73 12 12 0 0011.28-7.9l64-176a12 12 0 00-7.18-15.38z'></path>
  </svg>
)

const CommunitySVG = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    fill='currentColor'
    viewBox='0 0 256 256'
  >
    <path d='M164.38 181.1a52 52 0 10-72.76 0 75.89 75.89 0 00-30 28.89 12 12 0 0020.78 12 53 53 0 0191.22 0 12 12 0 1020.78-12 75.89 75.89 0 00-30.02-28.89zM100 144a28 28 0 1128 28 28 28 0 01-28-28zm147.21 9.59a12 12 0 01-16.81-2.39c-8.33-11.09-19.85-19.59-29.33-21.64a12 12 0 01-1.82-22.91 20 20 0 10-24.78-28.3 12 12 0 11-21-11.6 44 44 0 1173.28 48.35 92.18 92.18 0 0122.85 21.69 12 12 0 01-2.39 16.8zm-192.28-24c-9.48 2.05-21 10.55-29.33 21.65a12 12 0 01-19.19-14.45 92.37 92.37 0 0122.85-21.69 44 44 0 1173.28-48.35 12 12 0 11-21 11.6 20 20 0 10-24.78 28.3 12 12 0 01-1.82 22.91z'></path>
  </svg>
)

const DevHoursSVG = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    fill='currentColor'
    viewBox='0 0 256 256'
  >
    <path d='M168 120a12 12 0 01-5.12 9.83l-40 28A12 12 0 01104 148V92a12 12 0 0118.88-9.83l40 28A12 12 0 01168 120zm68-56v112a28 28 0 01-28 28H48a28 28 0 01-28-28V64a28 28 0 0128-28h160a28 28 0 0128 28zm-24 0a4 4 0 00-4-4H48a4 4 0 00-4 4v112a4 4 0 004 4h160a4 4 0 004-4zm-52 152H96a12 12 0 000 24h64a12 12 0 000-24z'></path>
  </svg>
)

const SupportSVG = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='18'
    fill='currentColor'
    viewBox='0 0 256 256'
  >
    <path d='M204.73 51.85A108.07 108.07 0 0020 128v56a28 28 0 0028 28h16a28 28 0 0028-28v-40a28 28 0 00-28-28H44.84A84.05 84.05 0 01128 44h.64a83.7 83.7 0 0182.52 72H192a28 28 0 00-28 28v40a28 28 0 0028 28h19.6a20 20 0 01-19.6 16h-56a12 12 0 000 24h56a44.05 44.05 0 0044-44v-80a107.34 107.34 0 00-31.27-76.15zM64 140a4 4 0 014 4v40a4 4 0 01-4 4H48a4 4 0 01-4-4v-44zm124 44v-40a4 4 0 014-4h20v48h-20a4 4 0 01-4-4z'></path>
  </svg>
)

const DeveloperDropdownWrapper = styled.div`
  padding: 2rem;
  width: 320px;
  box-sizing: border-box;
  ul {
    margin-left: -0.4rem;
    margin-top: 0;
  }
  li:not(:first-of-type) {
    margin-top: 0.125rem;
  }
  ${NavLink} {
    display: flex;
    padding: 0.375rem 0.5rem;
    span {
      margin-left: 2rem;
    }
    svg {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%) translateX(-50%);
      transition: 0.2s all;
      color: ${BrandColors.pigeon700};
    }
    &:hover {
      color: ${BrandColors.text};
      background-color: ${BrandColors.pigeon100};
      svg {
        color: ${BrandColors.primary};
      }
    }
  }
  ${Label} {
    margin-bottom: 1rem;
  }
`

const DevelopersDropdownLeft = (
  <DeveloperDropdownWrapper>
    <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
      Get Started
    </Label>
    <ul>
      <li>
        <NavLink href='/docs/' main>
          <Docs2SVG />
          <span>Documentation</span>
        </NavLink>
      </li>
      <li>
        <NavLink href='https://reference.flatfile.com/docs/api/' main>
          <APISVG />
          <span>API reference</span>
        </NavLink>
      </li>
    </ul>
  </DeveloperDropdownWrapper>
)

const DevelopersDropdownRight = (
  <DeveloperDropdownWrapper>
    <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
      Get Help
    </Label>
    <ul>
      <li>
        <NavLink href='/webinar/developer-office-hours/' main>
          <DevHoursSVG />
          <span>Developer Office Hours</span>
        </NavLink>
      </li>
      <li>
        <NavLink href='/join-slack/' main>
          <CommunitySVG />
          <span>Community</span>
        </NavLink>
      </li>
      <li>
        <NavLink href='/docs/overview?action=showintercom' main>
          <SupportSVG />
          <span>Support</span>
        </NavLink>
      </li>
    </ul>
  </DeveloperDropdownWrapper>
)

const CompanyDropdownWrapper = styled.div`
  padding: 32px;
  width: 318px;
  box-sizing: border-box;
  ul {
    margin-left: -0.4rem;
    margin-top: 0;
  }
  li:not(:first-of-type) {
    margin-top: 0.125rem;
  }
  ${NavLink} {
    display: flex;
    padding: 0.375rem 0.5rem;
    span {
      margin-left: 2rem;
    }
    svg, i {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%) translateX(-50%);
      transition: 0.2s all;
      color: ${BrandColors.pigeon700};
    }
    &:hover {
      color: ${BrandColors.text};
      background-color: ${BrandColors.pigeon100};
      svg, i {
        color: ${BrandColors.primary};
      }
    }
  }
  ${LabelTiny} {
    padding: 0.25rem;
    margin-left: 1rem;\
    color: ${BrandColors.primary};
    background-color: ${BrandColors.electric300};
  }
  ${Label} {
    margin-bottom: 1rem;
  }
`

const CompanyDropdown = (
  <CompanyDropdownWrapper>
    <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
      Get to know us
    </Label>
    <ul>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/about/'>
          <OfficeIconSm primaryColor='currentColor' />
          <span>About</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/news/'>
          <FaIcon
            color='currentColor'
            classes='fa-sharp fa-regular fa-bullhorn'
            size='1rem'
          />
          <span>News</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/whats-new/'>
          <FaIcon
            color='currentColor'
            classes='fa-sharp fa-regular fa-bullhorn'
            size='1rem'
          />
          <span>Product updates</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/resources/'>
          <BlogIconSm primaryColor='currentColor' />
          <span>Resource center</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink
          main
          svgHoverColor={BrandColors.sky600}
          to='/resources/case-studies/'
        >
          <HeartIconSm primaryColor='currentColor' />
          <span>Customers</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/blog/'>
          <BlogIconSm primaryColor='currentColor' />
          <span>Blog</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/security/'>
          <LockIconSm primaryColor='currentColor' />
          <span>Security</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/careers/'>
          <BriefcaseIconSm primaryColor='currentColor' />
          <span>Careers</span>
        </DocsLink>
      </li>
      <li>
        <DocsLink main svgHoverColor={BrandColors.sky600} to='/contact-us/'>
          <PhoneSm primaryColor='currentColor' />
          <span>Contact us</span>
        </DocsLink>
      </li>
    </ul>
  </CompanyDropdownWrapper>
)

const BlogPostDropdownWrapper = styled.div`
  padding: 32px;
  width: 318px;
  box-sizing: border-box;
  ${NavLink} {
    padding: 0.375rem 0.5rem;
  }
  ${Label} {
    margin: 0 0 1rem 0;
  }
  ${TitleXS} {
    max-height: 3.375rem;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
  .slick-dots {
    bottom: -2rem;
    left: -0.5rem;
    li {
      width: 4.5rem;
      height: 0.5rem;
      margin: 0;
      &:not(:first-of-type) {
        margin-left: 0.5625rem;
      }
    }
    li button {
      color: transparent;
      height: 0.5rem;
      width: 4.25rem;
    }
    li button::before {
      content: '';
      width: 4.25rem;
      height: 0.125rem;
      background-color: ${BrandColors.pigeon600};
      opacity: 1;
    }
    li.slick-active button::before {
      background-color: ${BrandColors.primary};
    }
  }
  ${NavLink} {
    margin-top: 2rem;
    svg {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }
    span {
      display: inline-block;
      margin-left: 1.75rem;
    }
    &:hover {
      color: ${BrandColors.text};
      background-color: ${BrandColors.pigeon200};
    }
  }
`
const BlogPostImage = styled.div<{
  backgroundImageSrc?: string
}>`
  height: 3.5rem;
  width: 6.25rem;
  margin-top: 0.1875rem;
  background-size: cover;
  background-position: center;
  ${({ backgroundImageSrc }) =>
    backgroundImageSrc &&
    css`
      background-image: url(${backgroundImageSrc});
    `}
`
const Slick = loadable(() => import('react-slick'))

// Helper to extract asset URL from local data
const getAssetUrl = (asset: any) => {
  if (!asset) return null
  const url = asset?.fields?.file?.url ?? asset?.file?.url ?? asset?.url
  return url?.startsWith('//') ? `https:${url}` : url
}

// Get 3 most recent posts from local data
const recentPosts = blogPostsData
  .filter(post => post.postTitle && post.seoUrlSlug)
  .slice(0, 3)

const RecentPosts = () => (
  <Slick
    arrows={false}
    autoplay={true}
    autoplaySpeed={5000}
    dots
    infinite={true}
    slidesToShow={1}
  >
    {recentPosts.map((post, index) => (
      <div key={`blog-post-${index}`}>
        <StyledLink
          hoverColor={BrandColors.text}
          to={`/blog/${post.seoUrlSlug}/`}
        >
          <Grid columns={2} spacing={0}>
            <Column>
              <BlogPostImage
                backgroundImageSrc={`${getAssetUrl(post.featuredImage)}?w=400`}
              />
            </Column>
            <Column>
              <TitleXS
                as='p'
                fontSize={0.875}
                fontWeight={400}
                lineHeight={1.29}
              >
                {post.postTitle}
              </TitleXS>
            </Column>
          </Grid>
        </StyledLink>
      </div>
    ))}
  </Slick>
)

const RecentWebinar = () => (
  <StyledLink
    hoverColor={BrandColors.text}
    to={`/webinar/data-integrations-surprising-missing-piece/`}
  >
    <Grid columns={2} spacing={0}>
      <Column>
        <BlogPostImage
          backgroundImageSrc={`/img/contentful/71YVhAG4-webinar-thumbnail.webp?w=150&h=106&q=75`}
        />
      </Column>
      <Column>
        <TitleXS as='p' fontSize={0.875} fontWeight={400} lineHeight={1.29}>
          Data integration's (surprising) missing piece
        </TitleXS>
      </Column>
    </Grid>
  </StyledLink>
)

const BlogPostDropdown = (
  <BlogPostDropdownWrapper>
    <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
      Latest webinar
    </Label>
    <RecentWebinar />
    <br />
    <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
      Latest posts
    </Label>
    <RecentPosts />
    <DocsLink main svgHoverColor={BrandColors.sky600} to='/blog/'>
      <BlogIconSm primaryColor='currentColor' />
      <span>Read more on the blog</span>
    </DocsLink>
  </BlogPostDropdownWrapper>
)

const LoginDropdownWrapper = styled.div`
  padding: 1rem 2rem 2rem;
  width: 240px;
  box-sizing: border-box;

  background: linear-gradient(
    to bottom,
    ${BrandColors.white} 0%,
    ${BrandColors.white} 45%,
    ${BrandColors.pigeon100} 45%,
    ${BrandColors.pigeon100} 100%
  );

  ul {
    margin-left: -0.4rem;
    margin-top: 1rem;
  }
  li:not(:first-of-type) {
    margin-top: 0.125rem;
  }
  ${NavLink} {
    display: flex;
    padding: 0.375rem 0.5rem;
    span {
      margin-left: 2rem;
    }
    svg {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%) translateX(-50%);
      transition: 0.2s all;
      color: ${BrandColors.pigeon700};
    }
    &:hover {
      color: ${BrandColors.text};
      background-color: ${BrandColors.pigeon100};
      svg {
        color: ${BrandColors.primary};
      }
    }
  }

  li.divider {
    margin-block: 0.75rem;
    padding-bottom: 0.5rem;
  }

  li h3 {
    padding-left: 0.5rem;
    margin-top: 1rem;
    margin-bottom: 0.75rem;
  }
`

const LoginIcon = () => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4'></path>
      <polyline points='10 17 15 12 10 7'></polyline>
      <line x1='15' x2='3' y1='12' y2='12'></line>
    </svg>
  )
}

const PlainText = styled.span`
  font-size: 0.875rem;
  color: ${BrandColors.pigeon700};
  padding-left: 0.5rem;
`

const LoginDropdown = (
  <LoginDropdownWrapper>
    <ul>
      <li>
        <DocsLink
          main
          svgHoverColor={BrandColors.sky600}
          href='https://platform.flatfile.com/oauth/dashboard'
        >
          <LoginIcon />
          <span>Platform</span>
        </DocsLink>
      </li>
      <li className='divider' aria-hidden='true'></li>
      <li>
        <Label color={BrandColors.pigeon700} letterSpacing='0.03em'>
          Unsupported Versions
        </Label>
      </li>
      <li>
        <PlainText>Flatfile v0.3</PlainText>
      </li>
    </ul>
  </LoginDropdownWrapper>
)

export const defaultLinkArray: ILink[] = [
  {
    children: [UseCasesColumn, PlatformDropdown],
    label: 'Platform',
    path: '/platform/'
  },
  {
    children: [DevelopersDropdownLeft, DevelopersDropdownRight],
    label: 'Developers',
    path: '/platform/build/'
  },
  {
    children: [CompanyDropdown, BlogPostDropdown],
    label: 'Company',
    path: '/about/'
  },
  {
    label: 'Pricing',
    path: '/pricing/'
  },
  {
    children: [LoginDropdown],
    label: 'Log in',
    path: 'https://platform.flatfile.com/oauth/dashboard'
  }
]

export const loginLinkArray: ILink[] = [
  {
    children: [LoginDropdown],
    label: 'Log in',
    path: 'https://platform.flatfile.com/oauth/dashboard'
  }
]

const mobileLinkArray: ILink[] = [
  {
    children: [],
    label: 'Platform',
    links: [
      {
        label: 'Platform Overview',
        path: '/platform/',
        external: false
      },
      {
        label: 'Projects',
        path: '/platform/projects/',
        external: false
      },
      {
        label: 'Portal',
        path: '/platform/portal/',
        external: false
      },
      {
        label: 'Workbooks',
        path: '/platform/workbooks/',
        external: false
      },
      {
        label: 'Mapping',
        path: '/platform/mapping/',
        external: false
      },
      {
        label: 'Orchestration',
        path: '/platform/orchestration/',
        external: false
      },
      {
        label: 'Plugins',
        path: '/plugins/',
        external: false
      }
    ],
    mobileOnly: true
  },
  {
    children: [],
    label: 'Flatfile For',
    links: [
      {
        external: false,
        label: 'Data Conversion & Onboarding',
        path: '/platform/use-cases/data-conversion/'
      },
      {
        external: false,
        label: 'Data Preparation & Migration',
        path: '/platform/use-cases/data-migration/'
      },
      {
        external: false,
        label: 'Embedded Data Import',
        path: '/platform/portal/'
      }
    ]
  },

  {
    children: [],
    label: 'For Developers',
    links: [
      {
        external: true,
        label: 'API Reference',
        path: 'https://reference.flatfile.com/docs/api/'
      },
      {
        external: true,
        label: 'API Status',
        path: 'https://status.flatfile.io/'
      },
      {
        external: true,
        label: 'Documentation',
        path: '/docs/'
      },
      {
        external: false,
        label: 'Developer Office Hours',
        path: '/developer-office-hours/'
      }
    ]
  },
  {
    children: [],
    label: 'Company',
    links: [
      {
        icon: OfficeIconSm,
        label: 'About',
        path: '/about/'
      },
      {
        icon: BlogIconSm,
        label: 'News',
        path: '/news/'
      },
      {
        icon: BlogIconSm,
        label: 'Product updates',
        path: '/whats-new/'
      },
      {
        icon: HeartIconSm,
        label: 'Customers',
        path: '/resources/case-studies/'
      },
      {
        icon: BlogIconSm,
        label: 'Blog',
        path: '/blog/'
      },
      {
        icon: BlogIconSm,
        label: 'Resource center',
        path: '/resources/'
      },
      {
        icon: LockIconSm,
        label: 'Security',
        path: '/security/'
      },
      {
        icon: BriefcaseIconSm,
        label: 'Careers',
        path: '/careers/'
      },
      {
        icon: PhoneSm,
        label: 'Contact us',
        path: '/contact-us/'
      }
    ]
  },
  {
    label: 'Pricing',
    path: '/pricing/'
  },
  {
    label: 'Log in',
    path: 'https://platform.flatfile.com/oauth/dashboard'
  }
]
export const LinkContainer = ({
  handleNavMouseEnter,
  handleNavMouseLeave,
  id,
  links
}: {
  handleNavMouseEnter?: any
  handleNavMouseLeave?: any
  id?: string
  links: ILink[]
}) => {
  const RenderedLinks = links
    .filter(link => !link.mobileOnly)
    .map((link, index) => {
      return (
        <InvertedContext.Consumer key={link.label}>
          {inverted => (
            <NavLI
              color={inverted ? BrandColors.white : BrandColors.text}
              data-label={link.label}
              key={`nav-item-0-${index}`}
              level={0}
              // onClick={link.links || link.children ? handleNavMouseEnter : null}
              // show nav item on hover
              onMouseEnter={handleNavMouseEnter}
              onMouseLeave={handleNavMouseLeave}
            >
              {link.path ? (
                <ExternalLink
                  href={link.path}
                  rel={link.rel}
                  target={link.target}
                >
                  {link.label}
                </ExternalLink>
              ) : (
                <DropdownLink
                  color={inverted ? BrandColors.white : BrandColors.text}
                >
                  {link.label}
                </DropdownLink>
              )}
            </NavLI>
          )}
        </InvertedContext.Consumer>
      )
    })
  return (
    <NavUL id={id} level={0}>
      {RenderedLinks}
    </NavUL>
  )
}
const DesktopActions = styled.div`
  display: none;

  @media (min-width: 1090px) {
    display: flex;
    margin-left: 2rem;
  }
`
export const ActionContainer = ({
  children,
  cta,
  inverted,
  nav
}: {
  children?: any
  cta?: ReactNode
  inverted?: boolean
  nav?: ReactNode
}) => {
  const label = demoCTA2.action.label
  const to = demoCTA2.action.path
  return (
    <>
      <DesktopActions>
        {nav && nav}
        {cta ? (
          cta
        ) : (
          <BrandButton
            className='ph-primaryCTA ph-primaryCTA--topNavbar'
            external
            inverted={inverted}
            label={label}
            to={to}
            size='small'
            fill={inverted ? 'white' : undefined}
            color={inverted ? 'black' : undefined}
          />
        )}
      </DesktopActions>
      {children && children}
    </>
  )
}
const MenuTriggerWrapper = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`
export const MenuTrigger = ({
  inverted,
  onClick
}: {
  inverted?: boolean
  onClick: (any) => void
}) => (
  <MenuTriggerWrapper
    aria-label='Hamburger Menu'
    onClick={onClick}
    role='button'
  >
    {inverted ? (
      <img
        alt=''
        height='16'
        src='/img/icons/menu-hamburger-white.svg'
        width='23'
      />
    ) : (
      <img alt='' height='16' src='/img/icons/menu-hamburger.svg' width='23' />
    )}
  </MenuTriggerWrapper>
)
const CloseButton = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  background-color: ${BrandColors.primary};
  color: ${props => (props.color ? props.color : BrandColors.white)};
  font-family: ${FontFamily.brandPrimary};
  font-size: 1rem;
  font-weight: 400;
  padding: 0.5rem 1rem;
`
const MobileActions = styled(ButtonGroup as any)`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  ${BrandButtonWrapper} {
    display: none;
  }
  @media (min-width: ${Size.small}) {
    ${BrandButtonWrapper} {
      display: inline-block;
    }
    ${MenuTriggerWrapper}, ${CloseButton} {
      margin-right: 1rem;
    }
  }
  @media (min-width: 1090px) {
    display: none;
  }
`
const Row = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 0;
`
const MobileMenuWrapper = styled.div`
  @media (max-width: ${Size.medium}) {
    background-color: ${BrandColors.white};
    box-shadow: ${BrandTokens.boxShadow8};
    border: 2px solid ${BrandColors.black};
    font-size: 1.1875rem;
    left: 0.5rem;
    opacity: 0;
    position: fixed;
    right: 0.5rem;
    top: 12rem;
    transition: opacity 0.1s ease-out, top 0.1s linear, scale 0.1s ease-out;
    transform: scale(0.5);
    visibility: hidden;
    &.show-menu-js {
      opacity: 1;
      top: 0.5rem;
      transform: scale(1);
      visibility: visible;
    }
    ${ButtonGroup} {
      background-color: ${BrandColors.pigeon100};
      padding: 1.5rem;
      & > ${BrandButtonWrapper} {
        white-space: pre;
      }
    }
  }
  @media (min-width: ${Size.medium}) {
    display: none;
  }
`
const MobileUL = styled.ul<{ level: number }>`
  ${props =>
    props.level === 0 &&
    css`
      padding: 1rem 1.5rem;
    `}
  ${props =>
    props.level === 1 &&
    css`
      padding-left: 1rem;
      padding-top: 0.5rem;
    `}
`
const MobileLI = styled.li<{ level: number }>`
  ${props =>
    props.level === 0 &&
    css`
      line-height: 1;
      margin-bottom: 0.5rem;
      padding-top: 1rem;
      padding-bottom: 0.5rem;
      a,
      p {
        color: ${BrandColors.text};
        align-items: center;
        display: flex;
        font-size: 1rem;
        font-weight: 400;
        justify-content: space-between;
      }
      & + & {
        border-top: 1px solid ${BrandColors.pigeon200};
      }
    `}
  ${props =>
    props.level === 1 &&
    css`
      line-height: 2;
    `}
`
const MobileDropdownContent = styled.div<{ active: boolean }>`
  display: none;
  ${props =>
    props.active &&
    css`
      display: block;
    `}
`
export const MobileLinkContainer = ({ links }: { links: ILink[] }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const ToggleMenu = event => {
    event.stopPropagation()
    event.preventDefault()
    return setShowMobileMenu(show => !show)
  }
  const ctaLabel = primaryCTA.action.label
  const ctaTo = primaryCTA.action.path
  return (
    <>
      <MobileActions>
        <InvertedContext.Consumer>
          {inverted => <MenuTrigger inverted={inverted} onClick={ToggleMenu} />}
        </InvertedContext.Consumer>
      </MobileActions>
      <MobileMenuWrapper className={showMobileMenu ? 'show-menu-js' : ''}>
        <Row>
          <img
            alt='Flatfile logo'
            height={24}
            loading='lazy'
            src='/img/contentful/33l3kWmP-flatfile-black.svg'
            width={104}
          />
          <CloseButton onClick={ToggleMenu}>Close</CloseButton>
        </Row>
        <MobileUL level={0}>
          {links
            .filter(link => !link.desktopOnly)
            .map((link, index) => {
              return (
                <MobileLI
                  key={`link-li-${index}`}
                  level={0}
                  onClick={() =>
                    setActiveDropdown(index === activeDropdown ? null : index)
                  }
                >
                  {' '}
                  {link.path ? (
                    <a href={link.path} rel={link.rel} target={link.target}>
                      {link.label}
                      {(link.children || link.links) && (
                        <svg
                          className='nav-icon'
                          height='20'
                          viewBox='0 0 24 24'
                          width='20'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <defs />
                          <g fill='none' fillRule='evenodd'>
                            <path d='M0 0h24v24H0z' />
                            <path
                              d='M17.29289922 8.2928912c.39052429-.3905243 1.02368927-.3905243 1.41421356 0 .39052429.3905243.39052429 1.0236893 0 1.4142136l-5.99999998 5.99999998c-.378579.37857894-.9881654.39182456-1.3828314.03004736l-6-5.50000004c-.407119-.3731924-.4346219-1.0057597-.0614295-1.4128787s1.0057597-.4346219 1.4128787-.0614295l5.2941997 4.8530163 5.32296892-5.322969z'
                              fill='currentColor'
                              fillRule='nonzero'
                            />
                          </g>
                        </svg>
                      )}
                    </a>
                  ) : (
                    <p>
                      {link.label}
                      {(link.children || link.links) && (
                        <svg
                          className='nav-icon'
                          height='20'
                          viewBox='0 0 24 24'
                          width='20'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <defs />
                          <g fill='none' fillRule='evenodd'>
                            <path d='M0 0h24v24H0z' />
                            <path
                              d='M17.29289922 8.2928912c.39052429-.3905243 1.02368927-.3905243 1.41421356 0 .39052429.3905243.39052429 1.0236893 0 1.4142136l-5.99999998 5.99999998c-.378579.37857894-.9881654.39182456-1.3828314.03004736l-6-5.50000004c-.407119-.3731924-.4346219-1.0057597-.0614295-1.4128787s1.0057597-.4346219 1.4128787-.0614295l5.2941997 4.8530163 5.32296892-5.322969z'
                              fill='currentColor'
                              fillRule='nonzero'
                            />
                          </g>
                        </svg>
                      )}
                    </p>
                  )}
                  {(link.links || link.children) && (
                    <MobileDropdownContent active={index === activeDropdown}>
                      {link.links && (
                        <MobileUL level={1}>
                          {link.links.map((subLink, idx) => (
                            <MobileLI key={`link-li-2-${idx}`} level={1}>
                              {subLink.path.startsWith('/') ? (
                                <Link to={subLink.path}>{subLink.label}</Link>
                              ) : (
                                <a
                                  href={subLink.path}
                                  rel={subLink.rel}
                                  target={subLink.target}
                                >
                                  {subLink.label}
                                </a>
                              )}
                            </MobileLI>
                          ))}
                        </MobileUL>
                      )}
                    </MobileDropdownContent>
                  )}
                </MobileLI>
              )
            })}
        </MobileUL>
        <Divider fill={FadeColor(BrandColors.white, 0.8)} margin='0' />
        <ButtonGroup>
          <BrandButton external label={ctaLabel} to={ctaTo} />
        </ButtonGroup>
      </MobileMenuWrapper>
    </>
  )
}
const DropdownContainer = styled.div<{
  activeDropdownSection: string
  column1Width?: string
  column2Width?: string
  offsetLeft: number
  visible: boolean
}>`
  position: absolute;
  overflow: hidden;
  top: 80px;
  /* if props.offsetLeft equals 50 do left: ${props => `${props.offsetLeft}%`};
  ortherwise do left: ${props => `${props.offsetLeft}px`}; */
  left: ${props =>
    props.offsetLeft === 50 ? `${props.offsetLeft}%` : `${props.offsetLeft}px`};
  transform: ${props =>
    props.offsetLeft === 50 ? 'translateX(-50%)' : 'none'};
  width: ${({ activeDropdownSection }) =>
    activeDropdownSection === 'Developers'
      ? '640px'
      : activeDropdownSection === 'Platform'
      ? '1024px'
      : activeDropdownSection === 'Log in'
      ? '240px'
      : '636px'};
  height: fit-content;
  max-width: 100%;
  ${({ visible }) =>
    !visible &&
    css`
      width: 0;
      height: 0;
    `}
  opacity: ${props => (props.visible ? '1' : '0')};
  background-color: ${BrandColors.white};
  border: 2px solid ${BrandColors.black};
  box-shadow: ${BrandTokens.boxShadow8};
  z-index: ${props => (props.visible ? '9999' : '-9999')};
  border-radius: 0;
  transition: opacity 250ms ease-in-out;

  /* if column1width exists set it  */
  ${props =>
    props.column1Width &&
    css`
      .dropdownColumn1 {
        width: ${props.column1Width};
      }
    `}
  /* if column2width exists set it  */
  ${props =>
    props.column2Width &&
    css`
      .dropdownColumn2 {
        width: ${props.column2Width};
      }
    `}
`
const DropdownColumn = styled.div`
  position: relative;
  transition: all 0.4s ease-in-out;
`
const DropdownColumnInset = styled.div`
  height: 100%;
  /* background-color: ${BrandColors.pigeon100}; */
  border-left: 2px solid ${BrandColors.black};
`
const DropdownColumnInner = styled.div<{
  active: boolean
}>`
  opacity: ${props => (props.active ? '1' : '0')};
  z-index: ${props => (props.active ? '1' : '0')};
  position: relative;
  transition: all 0.4s ease-in-out;
`
const DropdownWrapper = styled.div<{
  multipleColumns?: boolean
  visible: boolean
}>`
  display: ${({ multipleColumns }) => (multipleColumns ? 'flex' : 'block')};
  opacity: ${({ visible }) => (visible ? '1' : '0')};
  transition: all 250ms ease-in-out;
`
const DesktopDropdownContent = ({
  activeDropdownSection,
  links,
  multipleColumns = true,
  visible
}: {
  activeDropdownSection?: string
  links: ILink[]
  multipleColumns?: boolean
  visible: boolean
}) => {
  return (
    <DropdownWrapper multipleColumns={multipleColumns} visible={visible}>
      <DropdownColumn className='dropdownColumn1'>
        {links.map((link, index) =>
          link.children ? (
            <DropdownColumnInner
              active={link.label === activeDropdownSection}
              key={`dropdown-column-${index}-0`}
            >
              {link.children[0]}
            </DropdownColumnInner>
          ) : null
        )}
      </DropdownColumn>
      <DropdownColumn className='dropdownColumn2'>
        <DropdownColumnInset>
          {links.map((link, index) =>
            link.children ? (
              <DropdownColumnInner
                active={link.label === activeDropdownSection}
                key={`dropdown-column-${index}-1`}
              >
                {link.children[1]}
              </DropdownColumnInner>
            ) : null
          )}
        </DropdownColumnInset>
      </DropdownColumn>
    </DropdownWrapper>
  )
}
export const NavWrapper = styled.nav<{
  isScrolled?: boolean
}>`
  left: 0;
  padding: 2rem 0;
  position: absolute;
  right: 0;
  ${Container} > * {
    flex: 1;
  }
  ${TitleWrapper}, ${LogoWrapper} {
    margin-right: auto;
    text-align: left;
  }
  ${NavUL} {
    white-space: nowrap;
    display: flex;
  }
  ${DesktopActions} {
    margin-left: auto;
    justify-content: flex-end;
  }
  @media (min-width: ${Size.small}) {
    padding: 2rem 0;
  }
  ${({ isScrolled }) =>
    isScrolled &&
    css`
      ${DesktopActions} ${BrandButtonWrapper} {
        transition: transform 0.2s linear;
        background-color: ${BrandColors.primary};
        border-color: ${BrandColors.primary};
        color: ${BrandColors.white};
        &:hover {
          background-color: ${BrandColors.primary} !important;
        }
      }
    `}
`
const StickyNavWrapper = styled.div<{ compact: boolean; scrolled: boolean }>`
  border-bottom: 1px solid transparent;
  left: 0;
  position: absolute;
  right: 0;
  z-index: 100;
  ${props =>
    props.compact &&
    css`
      ${NavWrapper} {
        padding: 1rem 0;
      }
    `}
  ${props =>
    props.scrolled &&
    css`
      position: fixed;
      top: 0;
      ${NavWrapper} {
        padding: 1rem 0;
        background-color: ${BrandColors.white};
        border-bottom: 1px solid ${BrandColors.pigeon200};
      }
    `}
`

function useOutsideClick(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, callback])
}

const NavContainer = styled(Container)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  #link-container {
    gap: 0.25rem;
    margin-left: -1.5rem;
  }
`

export const NavBlock = ({
  children,
  compact,
  cta,
  inverted,
  pageTitle,
  path
}: {
  children?: ReactNode
  compact?: boolean
  cta?: ReactNode
  inverted?: boolean
  pageTitle?: string
  path?: string
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [productsOffsetLeft, setProductsOffsetLeft] = useState(0)
  const [developersOffsetLeft, setDevelopersOffsetLeft] = useState(0)
  const [companyOffsetLeft, setCompanyOffsetLeft] = useState(0)
  const [loginOffsetLeft, setLoginOffsetLeft] = useState(0)
  const [activeDropdownSection, setActiveDropdownSection] = useState(null)
  const [mouseOverNav, setMouseOverNav] = useState(false)
  const mouseOverNavRef = useRef(mouseOverNav)
  const navRef = useRef(null)

  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('md'))

  const setOffsets = () => {
    const linkContainerEl = document.getElementById(
      'link-container'
    ) as HTMLDivElement
    const linkContainerEl2 = document.getElementById(
      'link-container2'
    ) as HTMLDivElement
    // const productsWidth = 1224
    // const developersWidth = 640
    // const companyWidth = 636
    const loginWidth = 240
    // let productsOffset
    // let developersOffset
    // let companyOffset
    let loginOffset
    // if (linkContainerEl) {
    //   productsOffset = (productsWidth - linkContainerEl.offsetWidth) / 2
    //   developersOffset = (developersWidth - linkContainerEl.offsetWidth) / 2
    //   companyOffset = (companyWidth - linkContainerEl.offsetWidth) / 2
    // }
    if (linkContainerEl2) {
      loginOffset = (loginWidth - linkContainerEl2.offsetWidth) / 2
    }

    if (linkContainerEl) {
      setProductsOffsetLeft(50)
      setDevelopersOffsetLeft(50)
      setCompanyOffsetLeft(50)
    }
    if (linkContainerEl2) {
      setLoginOffsetLeft(linkContainerEl2.offsetLeft - loginOffset)
    }
  }

  useEffect(() => {
    setOffsets()
  }, [activeDropdownSection])

  useEffect(() => {
    mouseOverNavRef.current = mouseOverNav
  }, [mouseOverNav])

  const handleNavMouseEnter = event => {
    event.preventDefault()

    const li = event.currentTarget as HTMLLIElement
    // Remove the delay check since it's causing issues on first hover
    setMouseOverNav(true)
    setActiveDropdownSection(li.dataset.label)
    setDropdownOpen(true) // Immediately show dropdown

    // const li = event.currentTarget as HTMLLIElement
  }

  const handleNavMouseLeave = () => {
    setMouseOverNav(false)

    // Use a shorter timeout to make the UI more responsive
    window.setTimeout(() => {
      if (!mouseOverNavRef.current) {
        setDropdownOpen(false)
        setActiveDropdownSection(null)
      }
    }, 300) // Reduced from 600ms
  }

  const handleNavDropdownClose = () => {
    setMouseOverNav(false)
    setDropdownOpen(false)
    setActiveDropdownSection(null)
  }

  const handleDropdownMouseEnter = () => {
    setMouseOverNav(true)
  }

  const [isScrolled, setScrolled] = useState(false)
  const handleScroll = ev => {
    if (window.scrollY > 200) {
      setScrolled(true)
      setIsInverted(false)
    } else {
      setScrolled(false)
      setIsInverted(inverted)
    }
  }
  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  })

  const [isInverted, setIsInverted] = useState(inverted)

  useOutsideClick(navRef, handleNavDropdownClose)

  useEffect(() => {
    setIsInverted(inverted)
  }, [inverted])

  return (
    <InvertedContext.Provider value={isInverted}>
      <StickyNavWrapper ref={navRef} compact={compact} scrolled={isScrolled}>
        <NavWrapper isScrolled={isScrolled}>
          <NavContainer align='center'>
            {pageTitle ? (
              <LogoContainer pageTitle={pageTitle} path={path} />
            ) : (
              <LogoWrapper>
                <Link title='Flatfile.com | Back to top' to='/'>
                  <FlatfileLogoSVG
                    color={isInverted ? BrandColors.white : BrandColors.black}
                  />
                  {/* <img
                    alt='Flatfile logo'
                    height='24'
                    src={
                      isInverted
                        ? '/img/contentful/5DNClD4r-flatfile-white.svg'
                        : '/img/contentful/33l3kWmP-flatfile-black.svg'
                    }
                    style={{ marginTop: '5px' }}
                    width='104'
                  /> */}
                </Link>
              </LogoWrapper>
            )}
            <LinkContainer
              handleNavMouseEnter={handleNavMouseEnter}
              // handleNavMouseLeave={handleNavMouseLeave}
              id='link-container'
              links={defaultLinkArray}
            />
            {children ? (
              children
            ) : (
              <ActionContainer
                nav=<LinkContainer
                  handleNavMouseEnter={handleNavMouseEnter}
                  // handleNavMouseLeave={handleNavMouseLeave}
                  id='link-container2'
                  links={loginLinkArray}
                />
                cta={cta}
                inverted={inverted}
              />
            )}
            <MobileLinkContainer links={mobileLinkArray} />
          </NavContainer>
        </NavWrapper>
        {isLarge && (
          <>
            <DropdownContainer
              activeDropdownSection={'Platform'}
              column1Width='40%'
              column2Width='60%'
              id='products-dropdown'
              offsetLeft={productsOffsetLeft}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleNavMouseLeave}
              visible={dropdownOpen && activeDropdownSection === 'Platform'}
            >
              <DesktopDropdownContent
                activeDropdownSection={'Platform'}
                links={defaultLinkArray.filter(
                  link => link.label === 'Platform'
                )}
                visible={dropdownOpen && activeDropdownSection === 'Platform'}
              />
            </DropdownContainer>
            <DropdownContainer
              activeDropdownSection={'Developers'}
              id='developers-dropdown'
              offsetLeft={developersOffsetLeft}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleNavMouseLeave}
              visible={dropdownOpen && activeDropdownSection === 'Developers'}
            >
              <DesktopDropdownContent
                activeDropdownSection={'Developers'}
                links={defaultLinkArray.filter(
                  link => link.label === 'Developers'
                )}
                visible={dropdownOpen && activeDropdownSection === 'Developers'}
              />
            </DropdownContainer>
            <DropdownContainer
              activeDropdownSection={'Company'}
              id='company-dropdown'
              offsetLeft={companyOffsetLeft}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleNavMouseLeave}
              visible={dropdownOpen && activeDropdownSection === 'Company'}
            >
              <DesktopDropdownContent
                activeDropdownSection={'Company'}
                links={defaultLinkArray.filter(
                  link => link.label === 'Company'
                )}
                visible={dropdownOpen && activeDropdownSection === 'Company'}
              />
            </DropdownContainer>
            <DropdownContainer
              activeDropdownSection={'Log in'}
              id='login-dropdown'
              offsetLeft={loginOffsetLeft}
              onMouseEnter={handleDropdownMouseEnter}
              onMouseLeave={handleNavMouseLeave}
              visible={dropdownOpen && activeDropdownSection === 'Log in'}
            >
              <DesktopDropdownContent
                activeDropdownSection={'Log in'}
                links={defaultLinkArray.filter(link => link.label === 'Log in')}
                visible={dropdownOpen && activeDropdownSection === 'Log in'}
              />
            </DropdownContainer>
          </>
        )}
      </StickyNavWrapper>
    </InvertedContext.Provider>
  )
}
